import type { Contract } from '@ethersproject/contracts';
import type { JsonRpcProvider } from '@ethersproject/providers';

export enum ChainId {
  EthMainnet = 1,
  BscMainnet = 56,
  XDai = 100,
  MaticMainnet = 137,
}

export type ChainData = {
  id: ChainId;
  displayName: string;
  shortDisplayName: string;
  styleSlug: string;
  color: string;
  colorLight: string;
  colorDark: string;
  explorerUrl: string;
  exchangeUrl: string;
  provider: JsonRpcProvider;
  surfTokenContract: Contract;
};

export enum Token {
  ETH_DAI = '0x6b175474e89094c44da98b954eedeac495271d0f',
  ETH_WETH = '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
  ETH_SURF = '0xea319e87cf06203dae107dd8e5672175e3ee976c',
  ETH_WLEV = '0xa2482ccff8432ee68b9a26a30fcdd2782bd81bed',
  BSC_DAI = '0x1af3f329e8be154074d8769d1ffa4ee058b1dbc3',
  WBNB = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
  BSC_SURF = '0x0d1a15b277d25598144593836e5c6c20d427b61d',
  BSC_WLEV = '0x4272209eeb90d03a57f22fae365232eb323e3463',
  WXDAI = '0xe91d153e0b41518a2ce8dd3d7944fa863463a97d',
  XDAI_SURF = '0xc12956b840B403b600014a3092f6EBD9259738fE',
  XDAI_WLEV = '0xaCb954Db4F51558016455cE9b1Ba32a5bd8b529d',
  MATIC_DAI = '0x8f3cf7ad23cd3cadbd9735aff958023239c6a063',
  MATIC_WETH = '0x7ceb23fd6bc0add59e62ac25578270cff1b9f619',
  WMATIC = '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270',
  MATIC_SURF = '0x1E42EDbe5376e717C1B22904C59e406426E8173F',
  MATIC_WLEV = '0xEB7f7955730A7DBA1427A6567950eb4a98DfCbdF',
  TOWEL = '0x1E946cA17b893Ab0f22cF1951137624eE9E689EF',
  WAVE = '0x4de7fea447b837d7e77848a4b6c0662a64a84e14',
  RSURF = '0x5C8C83e5D5F7BE815863B810D45D7Bc706D7b15b',
}

export enum ContractAddress {
  Beach = '0x931BEE5052Fb77002E0Be26EB9340c44d77C03f7',
  Surf3d = '0xeb620A32Ea11FcAa1B3D70E4CFf6500B85049C97',
  Tito = '0x65e5bc985b8399b338c3c55ff1e3c048586d50ca',
  TowelStake = '0x817C521f9616204c24f05B2102E82f606Cd9A806',
  Whirlpool = '0x999b1e6EDCb412b59ECF0C5e14c20948Ce81F40b',
  PancakeSwap = '0x10ed43c718714eb63d5aa57b78b54704e256024e',
  Polywave = '0x8896b0e97d0ba384029fa0bdd8e35df6ac20803d',
}

export enum Currency {
  USD = 'USD',
  SURF = 'SURF',
  RSURF = 'rSURF',
  ETH = 'ETH',
  MATIC = 'MATIC',
  BNB = 'BNB',
  WLEV = 'wLEV',
  TOWEL = 'TOWEL',
  WAVE = 'WAVE',
  ETH_SURF_LP = 'ETH/SURF LP',
  WAVE_MATIC_LP = 'WAVE/MATIC LP',
  USDC = 'USDC',
  USDT = 'USDT',
  DAI = 'DAI',
  WMATIC = 'WMATIC',
  WETH = 'WETH',
  WBTC = 'WBTC',
  UNI = 'UNI',
  QUICK = 'QUICK',
  AAVE = 'AAVE',
  LINK = 'LINK',
  AGAR = 'AGAr',
  PCOMB = 'pCOMB',
  LMAO = 'LMAO',
  TITAN = 'TITAN',
  WRBT = 'wRBT',
}

export enum Pair {
  ETH_SURF = '0x32d588fd4d0993378995306563a04af5fa162dec',
  WLEV_ETH = '0x31153aa009229e2212f3b3f580cd6753930575b0',
  SURF_WXDAI = '0x5007f17b3901585082923c93d112dce0ecd8795d',
  SURF_RSURF = '0x4191ad32d1e61c1ca8f9cf5cec69682c47bdb9a4',
  RSURF_WXDAI = '0x51b9f404fad30bba58b1614a9fea89c2285f9c11',
  RSURF_WLEV = '0x208a55e9516b13aeec083132b97cfb20ecbc59a0',
  WMATIC_USDC = '0x6e7a5fafcec6bb1e78bae2a1f0b612012bf14827',
  WMATIC_WETH = '0xadbf1854e5883eb8aa7baf50705338739e558e5b',
  SURF_WETH = '0xba2d0a6addca0f82aa83652ea2f9c57683f83832',
  WMATIC_TOWEL = '0x9035d860f78f13006168a638658e7e94f1017658',
  WMATIC_WAVE = '0x1a3b6d6be3570dabe0e03a15a6f43e712520fd0d',
  WBNB_BUSD = '0x58f876857a02d6762e0101bb5c46a8c1ed44dc16',
  ETH_WBNB = '0x74e4716e431f45807dcf19f284c7aa99f18a4fbc',
  SURF_WBNB = '0x27a31115c58b207a7fa87d3c6e19ff62152bbf42',
}

export type GlobalStats = {
  totalSupply: number;
  totalBurnt: number;
  totalLocked: number;
  lpPrice: number;
  lpTokensLocked: number;
  lpTokensTotal: number;
  tokenReserve: number;
  otherReserve: number;
  whirlpoolBalance: number;
  whirlpoolTotalStaked: number;
  towelTotalSupply: number;
  towelTotalStaked: number;
};

export type CurrencyBalance = Partial<Record<Currency, number>>;

export type DappBalance = {
  locked: CurrencyBalance;
  unclaimed: CurrencyBalance;
};

export type PriceMapping = Partial<Record<Currency, Partial<Record<Currency, number>>>>;

type ExchangeGraphPair = {
  id: string;
  token0Price: string;
  token1Price: string;
  reserveETH?: string;
  reserveUSD?: string;
  totalSupply?: string;
};

export type ExchangeGraphResponse = {
  bundle?: {
    ethPrice: string;
  };
  pairs: ExchangeGraphPair[];
};
